import { clientLogger } from '@/lib/clientLogger';
import { useState, useRef, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '@/lib/api';
import { compressImage } from '@/lib/imageCompression';
import { useNotifications, notificationService } from './ui/NotificationSystem';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { Package, Plus, DollarSign, X, Edit2, Trash2, Check, ChevronDown, ChevronUp, Wrench, Paperclip } from 'lucide-react';
import { JobExpenseAttachmentsModal } from './JobExpenseAttachmentsModal';
import { ItemEditorDialog } from './ui/ItemEditorDialog';
import { NumericInput } from './ui/NumericInput';
import { JobDTO, JobExpenseDTO, ExpenseType } from '@/types';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { destructiveCopy } from '@/lib/destructiveConfirmMessages';

const EXPENSE_TYPE_CONFIG: Record<ExpenseType, { label: string; icon: React.ElementType; description: string }> = {
    CompanyParts: {
        label: 'Company Parts',
        icon: Package,
        description: 'Parts paid by the company',
    },
    TechnicianParts: {
        label: 'Technician Parts',
        icon: Wrench,
        description: 'Parts paid by the technician',
    },
};

interface JobExpensesCardProps {
    job: JobDTO;
    onJobUpdated?: (updatedJob: JobDTO) => void;
    compact?: boolean;
    /** When true, hides add/edit/delete actions */
    disabled?: boolean;
}

export function JobExpensesCard({ job, onJobUpdated, compact = false, disabled = false }: JobExpensesCardProps) {
    const { addNotification } = useNotifications();
    const queryClient = useQueryClient();
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingExpenseId, setEditingExpenseId] = useState<string | null>(null);
    const [newExpense, setNewExpense] = useState<Partial<JobExpenseDTO>>({
        type: 'CompanyParts',
        amount: 0,
        notes: '',
    });
    const [editExpense, setEditExpense] = useState<Partial<JobExpenseDTO>>({});
    const [isExpanded, setIsExpanded] = useState(false);
    const [receiptExpenseId, setReceiptExpenseId] = useState<string | null>(null);
    const [expenseToRemove, setExpenseToRemove] = useState<JobExpenseDTO | null>(null);

    // Inline attachment state for add-expense form
    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const [isUploadingReceipts, setIsUploadingReceipts] = useState(false);
    const addFormFileRef = useRef<HTMLInputElement>(null);

    // Calculate totals per type
    const totalByType = {
        CompanyParts: job.expenses?.filter(e => e.type === 'CompanyParts').reduce((sum, e) => sum + e.amount, 0) || 0,
        TechnicianParts: job.expenses?.filter(e => e.type === 'TechnicianParts').reduce((sum, e) => sum + e.amount, 0) || 0,
    };
    const totalExpenses = totalByType.CompanyParts + totalByType.TechnicianParts;

    // Common mutation success handler
    const handleMutationSuccess = (updatedJob: JobDTO, message: string) => {
        queryClient.setQueriesData({ queryKey: ['jobs'] }, (existing: any) => {
            if (!existing) return existing;
            const isArray = Array.isArray(existing);
            const items = isArray ? existing : existing.jobs;
            if (!Array.isArray(items)) return existing;
            const updatedItems = items.map((j: JobDTO) => (j.id === updatedJob.id ? updatedJob : j));
            if (isArray) return updatedItems;
            return { ...existing, jobs: updatedItems };
        });
        // Also update the single-job cache used by JobDetailsModal
        queryClient.setQueryData(['job', updatedJob.id], updatedJob);
        addNotification(notificationService.success(message));
        setShowAddForm(false);
        setEditingExpenseId(null);
        setNewExpense({
            type: 'CompanyParts',
            amount: 0,
            notes: '',
        });
        setEditExpense({});
        if (onJobUpdated) {
            onJobUpdated(updatedJob);
        }
    };

    // Upload pending receipt files for a newly-created expense
    const uploadPendingReceipts = useCallback(async (jobId: string, expenseId: string, files: File[]) => {
        setIsUploadingReceipts(true);
        try {
            // Compress images
            const compressed = await Promise.all(files.map(f => compressImage(f).then(c => ({ file: f, ...c }))));
            const fileNames = compressed.map(c => c.file.name);
            const presigned = await apiService.prepareExpenseAttachmentUpload(jobId, compressed.length, fileNames);
            const successful: any[] = [];
            for (let i = 0; i < presigned.length; i++) {
                const { upload_url, fields, attachment_id, object_key } = presigned[i];
                const c = compressed[i];
                const formData = new FormData();
                Object.entries(fields).forEach(([k, v]) => formData.append(k, v));
                formData.append('file', c.blob, c.file.name);
                const res = await fetch(upload_url, { method: 'POST', body: formData });
                if (res.ok) {
                    successful.push({
                        attachment_id, object_key,
                        mime_type: c.mimeType,
                        size_bytes: c.sizeBytes,
                        title: c.file.name.replace(/\.[^/.]+$/, ''),
                        job_expense_id: expenseId,
                    });
                }
            }
            if (successful.length > 0) {
                await apiService.completeExpenseAttachmentUpload(jobId, successful);
                queryClient.invalidateQueries({ queryKey: ['jobExpenseAttachments', jobId] });
            }
            addNotification(notificationService.success(`${successful.length} receipt${successful.length === 1 ? '' : 's'} uploaded`));
        } catch (err) {
            clientLogger.error('Receipt upload error:', err);
            addNotification(notificationService.error('Expense saved but receipt upload failed. Use the paperclip to retry.'));
        } finally {
            setIsUploadingReceipts(false);
        }
    }, [queryClient, addNotification]);

    // Add expense mutation
    const addExpenseMutation = useMutation({
        mutationFn: async (expense: Partial<JobExpenseDTO>) => {
            return apiService.mergePatchJob(job.id, {
                expense_operations: [{ operation: 'Add', expense: expense as JobExpenseDTO }]
            });
        },
        onSuccess: (updatedJob: JobDTO) => {
            // Find the newly created expense by diffing old vs new
            const oldIds = new Set((job.expenses || []).map(e => e.id).filter(Boolean));
            const newExpenseEntry = (updatedJob.expenses || []).find(e => e.id && !oldIds.has(e.id));
            const filesToUpload = [...pendingFiles];
            setPendingFiles([]);
            handleMutationSuccess(updatedJob, 'Expense added successfully');
            // Upload receipts in background if files were selected
            if (filesToUpload.length > 0 && newExpenseEntry?.id) {
                uploadPendingReceipts(job.id, newExpenseEntry.id, filesToUpload);
            }
        },
        onError: (err: any) => {
            addNotification(notificationService.error(err.message || 'Failed to add expense'));
        },
    });

    // Remove expense mutation
    const removeExpenseMutation = useMutation({
        mutationFn: async (expense: JobExpenseDTO) => {
            return apiService.mergePatchJob(job.id, {
                expense_operations: [{ operation: 'Remove', expense }]
            });
        },
        onSuccess: (updatedJob: JobDTO) => handleMutationSuccess(updatedJob, 'Expense removed successfully'),
        onError: (err: any) => {
            addNotification(notificationService.error(err.message || 'Failed to remove expense'));
        },
    });

    // Edit expense mutation
    const editExpenseMutation = useMutation({
        mutationFn: async (expense: Partial<JobExpenseDTO>) => {
            return apiService.mergePatchJob(job.id, {
                expense_operations: [{ operation: 'Edit', expense: expense as JobExpenseDTO }]
            });
        },
        onSuccess: (updatedJob: JobDTO) => handleMutationSuccess(updatedJob, 'Expense updated successfully'),
        onError: (err: any) => {
            addNotification(notificationService.error(err.message || 'Failed to update expense'));
        },
    });

    const handleAddExpense = () => {
        if (!newExpense.type || !newExpense.amount || newExpense.amount <= 0) {
            addNotification(notificationService.error('Please enter a valid amount'));
            return;
        }

        addExpenseMutation.mutate({
            ...newExpense,
        });
    };

    const handleStartEdit = (expenseIdOrIndex: string) => {
        setEditingExpenseId(expenseIdOrIndex);
        const expense = job.expenses?.find((e, i) => (e.id || `idx-${i}`) === expenseIdOrIndex);
        if (expense) {
            setEditExpense({ ...expense });
        }
        setShowAddForm(false);
    };

    const handleCancelEdit = () => {
        setEditingExpenseId(null);
        setEditExpense({});
    };

    const handleSaveEdit = (originalExpense: JobExpenseDTO) => {
        if (!editExpense.amount || editExpense.amount <= 0) {
            addNotification(notificationService.error('Please enter a valid amount'));
            return;
        }

        editExpenseMutation.mutate({
            id: originalExpense.id,
            type: editExpense.type,
            amount: editExpense.amount,
            notes: editExpense.notes || '',
        });
    };

    const handleRemoveExpenseClick = (expense: JobExpenseDTO) => {
        setExpenseToRemove(expense);
    };

    const getExpenseIcon = (type: ExpenseType) => {
        const config = EXPENSE_TYPE_CONFIG[type];
        return config?.icon || DollarSign;
    };

    const cardClasses = compact ? 'p-3' : 'p-4';
    const titleClasses = compact ? 'text-base' : 'text-m';
    const spacingClasses = compact ? 'space-y-3' : 'space-y-3';

    const isPending = addExpenseMutation.isPending || removeExpenseMutation.isPending || editExpenseMutation.isPending;

    const receiptModal = receiptExpenseId ? (
        <JobExpenseAttachmentsModal
            isOpen={true}
            onClose={() => setReceiptExpenseId(null)}
            jobId={job.id}
            expenseId={receiptExpenseId}
            onAttachmentsUpdated={() => { }}
        />
    ) : null;

    const removeExpenseConfirmDialog = (
        <ConfirmDialog
            isOpen={!!expenseToRemove}
            onClose={() => setExpenseToRemove(null)}
            onConfirm={async () => {
                if (expenseToRemove) {
                    await removeExpenseMutation.mutateAsync(expenseToRemove);
                }
            }}
            isLoading={removeExpenseMutation.isPending}
            title="Remove expense?"
            message={
                <div className="space-y-2">
                    <span>{destructiveCopy.removeExpense}</span>
                    <span className="block text-sm">{destructiveCopy.cannotUndo}</span>
                </div>
            }
            confirmText="Remove"
        />
    );

    const closeAddForm = () => {
        setShowAddForm(false);
        setPendingFiles([]);
    };

    const editingExpense = editingExpenseId
        ? job.expenses?.find((e, i) => (e.id || `idx-${i}`) === editingExpenseId)
        : undefined;

    const footerButtonBase = 'flex-1 sm:flex-none sm:min-w-[140px] px-4 py-2.5 text-sm font-semibold rounded-xl transition-colors';

    // Add / Edit Expense use the same floating window as Add Item.
    const expenseDialog = disabled ? null : showAddForm ? (
        <ItemEditorDialog
            title="Add Expense"
            icon={DollarSign}
            onClose={closeAddForm}
            footer={
                <div className="flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={closeAddForm}
                        className={`${footerButtonBase} text-gray-700 bg-white border border-gray-300 hover:bg-gray-50`}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleAddExpense}
                        disabled={isPending || isUploadingReceipts || !newExpense.amount || newExpense.amount <= 0}
                        className={`${footerButtonBase} flex items-center justify-center gap-2 text-white bg-primary-600 shadow-sm hover:bg-primary-700 disabled:opacity-50`}
                    >
                        {(addExpenseMutation.isPending || isUploadingReceipts) ? <LoadingSpinner /> : <Check className="h-4 w-4" />}
                        {isUploadingReceipts ? 'Uploading...' : pendingFiles.length > 0 ? `Save & Upload ${pendingFiles.length}` : 'Save Expense'}
                    </button>
                </div>
            }
        >
            {renderExpenseFields(newExpense, setNewExpense, true)}
        </ItemEditorDialog>
    ) : editingExpense ? (
        <ItemEditorDialog
            title="Edit Expense"
            icon={DollarSign}
            onClose={handleCancelEdit}
            footer={
                <div className="flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={handleCancelEdit}
                        className={`${footerButtonBase} text-gray-700 bg-white border border-gray-300 hover:bg-gray-50`}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() => handleSaveEdit(editingExpense)}
                        disabled={isPending}
                        className={`${footerButtonBase} flex items-center justify-center gap-2 text-white bg-primary-600 shadow-sm hover:bg-primary-700 disabled:opacity-50`}
                    >
                        {editExpenseMutation.isPending ? <LoadingSpinner /> : <Check className="h-4 w-4" />}
                        Save Changes
                    </button>
                </div>
            }
        >
            {renderExpenseFields(editExpense, setEditExpense, false)}
        </ItemEditorDialog>
    ) : null;

    // Expense form fields, laid out like the Add Item window.
    function renderExpenseFields(
        values: Partial<JobExpenseDTO>,
        setValues: React.Dispatch<React.SetStateAction<Partial<JobExpenseDTO>>>,
        withReceipts: boolean,
    ) {
        const inputClass = 'w-full min-w-0 px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500';
        const labelClass = 'block text-sm font-semibold text-gray-800 mb-1.5';
        const selectedType = values.type || 'CompanyParts';

        return (
            <div className="space-y-4">
                {/* Type */}
                <div>
                    <label className={labelClass}>Type</label>
                    <div className="grid grid-cols-2 gap-3">
                        {(Object.keys(EXPENSE_TYPE_CONFIG) as ExpenseType[]).map((key) => {
                            const config = EXPENSE_TYPE_CONFIG[key];
                            const TypeIcon = config.icon;
                            const selected = selectedType === key;
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => setValues(prev => ({ ...prev, type: key }))}
                                    className={`flex items-center gap-3 rounded-xl border px-3 py-3 text-left transition-colors ${selected
                                        ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
                                        : 'border-gray-300 bg-white hover:bg-gray-50'}`}
                                >
                                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${selected ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
                                        <TypeIcon className="h-4 w-4" />
                                    </span>
                                    <span className="min-w-0">
                                        <span className={`block text-sm font-semibold ${selected ? 'text-primary-700' : 'text-gray-900'}`}>{config.label}</span>
                                        <span className="hidden sm:block text-xs text-gray-500">{config.description}</span>
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Amount */}
                <div>
                    <label className={labelClass}>
                        Amount <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">$</span>
                        <NumericInput
                            step="0.01"
                            min="0"
                            value={values.amount || 0}
                            onChange={(val) => setValues(prev => ({ ...prev, amount: val }))}
                            className={`${inputClass} pl-7`}
                            placeholder="0.00"
                        />
                    </div>
                </div>

                {/* Notes */}
                <div>
                    <label className={labelClass}>Notes</label>
                    <textarea
                        value={values.notes || ''}
                        onChange={(e) => setValues(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="Optional description"
                        rows={3}
                        className={`${inputClass} resize-y`}
                    />
                </div>

                {/* Receipts (new expenses only; existing ones use the paperclip) */}
                {withReceipts && (
                    <div>
                        <label className={labelClass}>Receipts <span className="font-normal text-gray-500">(optional)</span></label>
                        <input
                            ref={addFormFileRef}
                            type="file"
                            multiple
                            accept="image/*,application/pdf"
                            onChange={(e) => {
                                if (e.target.files) {
                                    const MAX_PDF = 10 * 1024 * 1024;
                                    const valid = Array.from(e.target.files).filter(f => {
                                        if (f.type === 'application/pdf') return f.size <= MAX_PDF;
                                        return f.type.startsWith('image/') && f.size <= MAX_PDF;
                                    });
                                    setPendingFiles(prev => [...prev, ...valid].slice(0, 10));
                                }
                                e.target.value = '';
                            }}
                            className="hidden"
                        />
                        {pendingFiles.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-2">
                                {pendingFiles.map((f, i) => (
                                    <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 text-xs rounded-full border border-blue-200">
                                        {f.name.length > 20 ? f.name.slice(0, 17) + '...' : f.name}
                                        <button
                                            type="button"
                                            onClick={() => setPendingFiles(prev => prev.filter((_, j) => j !== i))}
                                            className="hover:text-red-500"
                                            aria-label="Remove file"
                                        >
                                            <X className="h-3 w-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={() => addFormFileRef.current?.click()}
                            className="w-full flex items-center justify-center gap-2 px-3 py-4 text-sm border-2 border-dashed border-gray-300 rounded-xl hover:border-primary-400 hover:bg-primary-50 text-gray-500 hover:text-primary-600 transition-colors"
                        >
                            <Paperclip className="h-4 w-4" />
                            {pendingFiles.length > 0 ? 'Add more photos' : 'Attach receipt photos'}
                        </button>
                    </div>
                )}

                {/* Amount summary, like the item Line Total */}
                <div className="flex items-center justify-between rounded-xl bg-gray-100 px-4 py-4">
                    <span className="text-sm font-medium text-gray-600">Expense Total</span>
                    <span className="text-2xl font-bold text-gray-900">${(values.amount || 0).toFixed(2)}</span>
                </div>
            </div>
        );
    }

    // Compact mode: render content directly without collapsible wrapper
    if (compact) {
        return (
            <>
                <div className={`${cardClasses} ${spacingClasses}`}>
                    {/* Add Expense Button */}
                    {!disabled && !showAddForm && !editingExpenseId && (
                        <button
                            type="button"
                            onClick={() => setShowAddForm(true)}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors"
                        >
                            <Plus className="h-4 w-4" />
                            Add Expense
                        </button>
                    )}

                    {/* Existing expenses */}
                    {job.expenses && job.expenses.length > 0 && (
                        <div className={spacingClasses}>
                            {job.expenses.map((expense, index) => {
                                const expenseKey = expense.id || `idx-${index}`;
                                const Icon = getExpenseIcon(expense.type);

                                return (
                                    <div key={expenseKey} className="flex items-center justify-between bg-gray-50 rounded-lg p-2 border border-gray-200 group hover:border-gray-300 transition-colors">
                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                            <div className="p-1.5 bg-gray-100 rounded flex-shrink-0">
                                                <Icon className="h-3.5 w-3.5 text-gray-600" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <span className="text-sm font-medium">{EXPENSE_TYPE_CONFIG[expense.type]?.label || expense.type}</span>
                                                {expense.notes && (
                                                    <p className="text-xs text-gray-500 truncate">{expense.notes}</p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-semibold text-orange-600">${expense.amount.toFixed(2)}</span>
                                            {!disabled && (
                                                <div className="flex gap-1">
                                                    {expense.id && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setReceiptExpenseId(expense.id!)}
                                                            className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-primary-600"
                                                            title="Receipts"
                                                        >
                                                            <Paperclip className="h-3.5 w-3.5" />
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => handleStartEdit(expenseKey)}
                                                        disabled={isPending || showAddForm}
                                                        className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-primary-600 disabled:opacity-50"
                                                        title="Edit"
                                                    >
                                                        <Edit2 className="h-3.5 w-3.5" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveExpenseClick(expense)}
                                                        disabled={isPending}
                                                        className="p-1 hover:bg-red-50 rounded text-gray-400 hover:text-red-600 disabled:opacity-50"
                                                        title="Remove"
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Empty state */}
                    {(!job.expenses || job.expenses.length === 0) && !showAddForm && (
                        <p className="text-sm text-gray-500 text-center py-4">No expenses recorded</p>
                    )}

                    {/* Total */}
                    {totalExpenses > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-700">Total Expenses</span>
                                <span className="text-sm font-semibold text-orange-600">${totalExpenses.toFixed(2)}</span>
                            </div>
                        </div>
                    )}
                </div>
                {expenseDialog}
                {receiptModal}
                {removeExpenseConfirmDialog}
            </>
        );
    }

    // Full mode with collapsible
    return (
        <>
            <div className={`bg-gray-50 rounded-lg ${isExpanded ? cardClasses : 'p-3'} transition-all duration-200`}>
                {/* Header */}
                <div
                    className={`flex items-center justify-between cursor-pointer ${isExpanded ? 'mb-3' : ''}`}
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    <div className="flex items-center gap-4">
                        <h3 className={`${titleClasses} font-semibold text-gray-900 flex items-center gap-2`}>
                            <DollarSign className="h-5 w-5 text-orange-500" />
                            Job Expenses
                        </h3>

                        {/* Collapsed Summary */}
                        {!isExpanded && (
                            <div className="flex items-center gap-3 text-sm hidden sm:flex">
                                <div className="w-px h-5 bg-gray-300 mx-2"></div>
                                <span className="text-gray-600 font-semibold">Total: <span className="font-medium text-orange-600">${totalExpenses.toFixed(2)}</span></span>
                                {totalByType.CompanyParts > 0 && (
                                    <span className="text-gray-500 text-xs font-semibold">Co: ${totalByType.CompanyParts.toFixed(2)}</span>
                                )}
                                {totalByType.TechnicianParts > 0 && (
                                    <span className="text-gray-500 text-xs font-semibold">Tech Parts: ${totalByType.TechnicianParts.toFixed(2)}</span>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Right side - Add button and Chevron */}
                    <div className="flex items-center gap-2">
                        {!disabled && !showAddForm && !editingExpenseId && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowAddForm(true);
                                }}
                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50 rounded transition-colors"
                            >
                                <Plus className="h-3.5 w-3.5" />
                                Add Expense
                            </button>
                        )}
                        {isExpanded ? <ChevronUp className="h-5 w-5 text-gray-500" /> : <ChevronDown className="h-5 w-5 text-gray-500" />}
                    </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                    <>
                        {/* Summary by type */}
                        {totalExpenses > 0 && (
                            <div className="flex justify-between items-center mb-3 pb-3 border-b border-gray-200">
                                <div className="text-sm space-x-4">
                                    {totalByType.CompanyParts > 0 && (
                                        <span className="text-gray-600">
                                            <Package className="h-3 w-3 inline mr-1" />
                                            Company: <span className="font-medium text-gray-900">${totalByType.CompanyParts.toFixed(2)}</span>
                                        </span>
                                    )}
                                    {totalByType.TechnicianParts > 0 && (
                                        <span className="text-gray-600">
                                            <Wrench className="h-3 w-3 inline mr-1" />
                                            Tech Parts: <span className="font-medium text-gray-900">${totalByType.TechnicianParts.toFixed(2)}</span>
                                        </span>
                                    )}
                                </div>
                                <div className="text-sm">
                                    <span className="text-gray-600 font-semibold">Total: </span>
                                    <span className="font-medium text-orange-600">${totalExpenses.toFixed(2)}</span>
                                </div>
                            </div>
                        )}

                        {/* Existing expenses */}
                        {job.expenses && job.expenses.length > 0 && (
                            <div className={spacingClasses}>
                                {job.expenses.map((expense, index) => {
                                    const expenseKey = expense.id || `idx-${index}`;
                                        const Icon = getExpenseIcon(expense.type);

                                    return (
                                        <div key={expenseKey} className="flex items-center justify-between bg-white rounded-lg p-2 border border-gray-200 group hover:border-gray-300 transition-colors">
                                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                                <div className="p-1.5 bg-gray-100 rounded flex-shrink-0">
                                                    <Icon className="h-3.5 w-3.5 text-gray-600" />
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <span className="text-sm font-medium">{EXPENSE_TYPE_CONFIG[expense.type]?.label || expense.type}</span>
                                                    {expense.notes && (
                                                        <p className="text-xs text-gray-500 truncate">{expense.notes}</p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-semibold text-orange-600">${expense.amount.toFixed(2)}</span>
                                                {!disabled && (
                                                    <div className="flex gap-1">
                                                        {expense.id && (
                                                            <button
                                                                type="button"
                                                                onClick={() => setReceiptExpenseId(expense.id!)}
                                                                className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-primary-600"
                                                                title="Receipts"
                                                            >
                                                                <Paperclip className="h-3.5 w-3.5" />
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            onClick={() => handleStartEdit(expenseKey)}
                                                            disabled={isPending || showAddForm}
                                                            className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-primary-600 disabled:opacity-50"
                                                            title="Edit"
                                                        >
                                                            <Edit2 className="h-3.5 w-3.5" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveExpenseClick(expense)}
                                                            disabled={isPending}
                                                            className="p-1 hover:bg-red-50 rounded text-gray-400 hover:text-red-600 disabled:opacity-50"
                                                            title="Remove"
                                                        >
                                                            <Trash2 className="h-3.5 w-3.5" />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Empty state */}
                        {(!job.expenses || job.expenses.length === 0) && !showAddForm && (
                            <p className="text-sm text-gray-500 text-center py-4">No expenses recorded</p>
                        )}
                    </>
                )}
            </div>
            {expenseDialog}
            {receiptModal}
            {removeExpenseConfirmDialog}
        </>
    );
}
